import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { toast } from 'react-hot-toast';
import {
  FileText,
  Download,
  Target,
  BookOpen,
  Heart,
  Sparkles,
  X,
  Save,
  ArrowLeft,
  CheckCircle,
  ArrowRight,
  Eye
} from 'lucide-react';
import ShareButton from '../Sharing/ShareButton';
import ThematicReviewModal from './ThematicReviewModal';

const TeacherBragSheetFramework = ({
  recommendationId: propRecommendationId,
  recommendation: propRecommendation,
  onClose,
  genericBragSheetData = {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { currentUser } = useFamilyContext();

  // Get recommendationId from props, params, or location state
  const recommendationId = propRecommendationId || params.recommendationId || location.state?.recommendationId;
  const [recommendation, setRecommendation] = useState(propRecommendation || location.state?.recommendation || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showThematicReview, setShowThematicReview] = useState(false);
  const [thematicAnalysis, setThematicAnalysis] = useState(null);
  const [analyzingTheme, setAnalyzingTheme] = useState(false);
  const [completionScore, setCompletionScore] = useState(0);
  const [workflowState, setWorkflowState] = useState('not_started');
  const hasLoadedRef = React.useRef(false);
  const [teacherBragSheetData, setTeacherBragSheetData] = useState({
    // Copy from generic brag sheet
    basicInfo: {},
    academicHighlights: {},
    achievements: [],
    extracurriculars: [],
    personalQualities: [],
    challengesOvercome: [],
    leadershipContexts: {},
    personalBrand: '',

    // Teacher-specific fields
    collegeGoals: '',
    classroomMoments: [],
    unexpectedDetails: [],
    thankYouNote: '',

    // Section exclusions
    sectionExclusions: {
      basicInfo: false,
      academicHighlights: false,
      collegeGoals: false,
      classroomMoments: false,
      leadershipContexts: false,
      achievements: false,
      timelineGrowth: false,
      unexpectedDetails: false,
      thankYouNote: false,
      personalBrand: false
    }
  });

  // Load existing teacher-specific brag sheet data or copy from generic
  useEffect(() => {
    const loadTeacherBragSheetData = async () => {
      // Prevent multiple loads
      if (hasLoadedRef.current) return;

      try {
        setLoading(true);
        hasLoadedRef.current = true;

        // Try to load existing teacher-specific data including recommendation details
        const { data: existingData, error } = await supabase
          .from('college_recommendation_letters')
          .select('*')
          .eq('id', recommendationId)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        // Set recommendation data if we loaded it
        if (existingData && !recommendation) {
          setRecommendation(existingData);
        }

        // Load cached thematic analysis if available
        if (existingData?.thematic_analysis_results) {
          console.log('📊 Loaded cached teacher-specific thematic analysis from', existingData.thematic_analysis_date);
          setThematicAnalysis(existingData.thematic_analysis_results);
        }

        // Load completion score and workflow state
        setCompletionScore(existingData?.brag_sheet_completion_score || 0);
        setWorkflowState(existingData?.brag_sheet_workflow_state || 'not_started');

        let initialData;
        if (existingData?.brag_sheet_data && Object.keys(existingData.brag_sheet_data).length > 0) {
          // Use existing teacher-specific data
          initialData = existingData.brag_sheet_data;
        } else {
          // Copy from generic brag sheet and add teacher-specific fields
          initialData = {
            ...genericBragSheetData,
            collegeGoals: '',
            classroomMoments: [],
            unexpectedDetails: [],
            thankYouNote: '',
            sectionExclusions: {
              basicInfo: false,
              academicHighlights: false,
              collegeGoals: false,
              classroomMoments: false,
              leadershipContexts: false,
              achievements: false,
              timelineGrowth: false,
              unexpectedDetails: false,
              thankYouNote: false,
              personalBrand: false
            }
          };
        }

        setTeacherBragSheetData(initialData);
      } catch (error) {
        console.error('Error loading teacher brag sheet data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (recommendationId) {
      loadTeacherBragSheetData();
    }
  }, [recommendationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate completion score based on filled sections
  const calculateCompletionScore = (data) => {
    let totalWeight = 0;
    let completedWeight = 0;

    // Basic Info (15 points)
    totalWeight += 15;
    const basicInfoFields = ['name', 'gradeLevel', 'school', 'intendedMajor'];
    const filledBasicInfo = basicInfoFields.filter(field => data.basicInfo?.[field]).length;
    completedWeight += (filledBasicInfo / basicInfoFields.length) * 15;

    // Academic Highlights (10 points)
    totalWeight += 10;
    const academicFields = ['gpa', 'classRank', 'testScores'];
    const filledAcademic = academicFields.filter(field => data.academicHighlights?.[field]).length;
    completedWeight += (filledAcademic / academicFields.length) * 10;

    // College Goals (15 points)
    totalWeight += 15;
    if (data.collegeGoals && data.collegeGoals.length > 50) {
      completedWeight += 15;
    } else if (data.collegeGoals && data.collegeGoals.length > 0) {
      completedWeight += 7;
    }

    // Classroom Moments (25 points) - Most important section
    totalWeight += 25;
    const classroomMoments = data.classroomMoments || [];
    if (classroomMoments.length >= 3) {
      completedWeight += 25;
    } else if (classroomMoments.length >= 2) {
      completedWeight += 17;
    } else if (classroomMoments.length >= 1) {
      completedWeight += 10;
    }

    // Achievements (10 points)
    totalWeight += 10;
    const achievements = data.achievements || [];
    if (achievements.length >= 3) {
      completedWeight += 10;
    } else if (achievements.length > 0) {
      completedWeight += (achievements.length / 3) * 10;
    }

    // Unexpected Details (10 points)
    totalWeight += 10;
    const unexpectedDetails = data.unexpectedDetails || [];
    if (unexpectedDetails.length >= 2) {
      completedWeight += 10;
    } else if (unexpectedDetails.length >= 1) {
      completedWeight += 5;
    }

    // Thank You Note (15 points)
    totalWeight += 15;
    if (data.thankYouNote && data.thankYouNote.length > 100) {
      completedWeight += 15;
    } else if (data.thankYouNote && data.thankYouNote.length > 0) {
      completedWeight += 7;
    }

    const score = Math.round((completedWeight / totalWeight) * 100);
    return Math.min(score, 100);
  };

  // Auto-save function with score calculation
  const autoSave = async (newData) => {
    try {
      setSaving(true);

      // Calculate new completion score
      const newScore = calculateCompletionScore(newData);
      setCompletionScore(newScore);

      // Determine workflow state based on score
      let newWorkflowState = workflowState;
      if (newScore >= 70 && workflowState === 'in_progress') {
        newWorkflowState = 'ready';
      } else if (newScore > 0 && workflowState === 'not_started') {
        newWorkflowState = 'in_progress';
      }
      setWorkflowState(newWorkflowState);

      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          brag_sheet_data: newData,
          brag_sheet_updated: new Date().toISOString(),
          brag_sheet_completion_score: newScore,
          brag_sheet_workflow_state: newWorkflowState
        })
        .eq('id', recommendationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving teacher brag sheet:', error);
    } finally {
      setSaving(false);
    }
  };

  // Update handlers
  const updateField = (field, value) => {
    const newData = { ...teacherBragSheetData, [field]: value };
    setTeacherBragSheetData(newData);
    autoSave(newData);
  };

  const addArrayItem = (field, item) => {
    const newArray = [...(teacherBragSheetData[field] || []), item];
    const newData = { ...teacherBragSheetData, [field]: newArray };
    setTeacherBragSheetData(newData);
    autoSave(newData);
  };

  const removeArrayItem = (field, index) => {
    const newArray = (teacherBragSheetData[field] || []).filter((_, i) => i !== index);
    const newData = { ...teacherBragSheetData, [field]: newArray };
    setTeacherBragSheetData(newData);
    autoSave(newData);
  };

  const toggleSectionExclusion = (section, excluded) => {
    const newExclusions = {
      ...(teacherBragSheetData.sectionExclusions || {}),
      [section]: excluded
    };
    const newData = { ...teacherBragSheetData, sectionExclusions: newExclusions };
    setTeacherBragSheetData(newData);
    autoSave(newData);
  };

  // Button Handlers
  const handleSaveAndExit = async () => {
    // Save is already happening via autoSave, just close
    toast.success('Brag sheet saved successfully');
    if (onClose) {
      onClose();
    } else {
      navigate('/college/recommendations');
    }
  };

  const handleMarkComplete = async () => {
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          brag_sheet_workflow_state: 'ready'
        })
        .eq('id', recommendationId);

      if (error) throw error;

      setWorkflowState('ready');
      toast.success('Brag sheet marked as complete!');

      // Navigate back to recommendations hub
      navigate('/college/recommendations');
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Failed to mark as complete');
    }
  };

  const handleContinueNext = () => {
    // Navigate to FollowUpEmailComposer with recommendation context
    navigate('/college/recommendations/follow-up-note', {
      state: {
        recommendation: recommendation,
        recommendationId: recommendationId,
        bragSheetData: teacherBragSheetData,
        fromBragSheet: true
      }
    });
  };

  // Handle thematic coherence analysis
  const handleThematicAnalysis = async () => {
    // If we have cached analysis, show it and ask if they want to re-run
    if (thematicAnalysis && !analyzingTheme) {
      const reAnalyze = window.confirm('You have a saved analysis. Would you like to re-run the analysis with your current brag sheet content?');
      if (!reAnalyze) {
        setShowThematicReview(true);
        return;
      }
    }

    // First, get the student's intended major from their profile
    const { data: profileData, error } = await supabase
      .from('student_academic_profiles')
      .select('intended_major, academic_interests')
      .eq('user_id', currentUser?.id)
      .single();

    if (error || !profileData?.intended_major) {
      alert('Please add your intended major to your student profile first');
      return;
    }

    setAnalyzingTheme(true);
    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'analyze-thematic-coherence',
          bragSheetData: teacherBragSheetData,
          intendedMajor: profileData.intended_major,
          academicInterests: profileData.academic_interests || [],
          analysisMode: 'recommendation-request',
          userId: currentUser?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          setThematicAnalysis(data.analysis);
          setShowThematicReview(true);

          // Save analysis results to database
          try {
            await supabase
              .from('college_recommendation_letters')
              .update({
                thematic_analysis_results: data.analysis,
                thematic_analysis_date: new Date().toISOString()
              })
              .eq('id', recommendationId);

            console.log('✅ Saved teacher-specific thematic analysis to database');
          } catch (dbError) {
            console.error('Failed to save analysis to database:', dbError);
            // Don't show error to user - analysis still works
          }
        } else {
          alert('Failed to analyze thematic coherence. Please try again.');
        }
      } else {
        const error = await response.json();
        console.error('Analysis error:', error);
        alert(`Failed to analyze: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error analyzing thematic coherence:', error);
      alert('An error occurred while analyzing. Please try again.');
    } finally {
      setAnalyzingTheme(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-azure-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-stone-600">Loading brag sheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Sticky Header with Progress Bar */}
      <div className="sticky top-0 z-20 bg-gradient-to-br from-slate-50 to-stone-100 pb-4 -mx-6 px-6 -mt-6 pt-6 mb-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveAndExit}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Brag Sheet Builder
                </h1>
                <p className="text-stone-600">
                  For {recommendation?.recommender_name} - {recommendation?.subject || recommendation?.recommender_type}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThematicAnalysis}
                disabled={analyzingTheme}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-violet-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 relative"
                title="Analyze how well this brag sheet helps your teacher write about your academic interests"
              >
                <Sparkles className="h-4 w-4" />
                {analyzingTheme ? 'Analyzing...' : (thematicAnalysis ? 'Review Analysis' : 'Review Theme')}
                {thematicAnalysis && !analyzingTheme && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white"></span>
                )}
              </button>
              <ShareButton
                itemType="teacher_brag_sheet"
                itemId={recommendationId}
                itemTitle={`Brag Sheet for ${recommendation?.recommender_name}`}
                variant="compact"
              />
            </div>
          </div>

          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-azure-800">
              <strong>Teacher-Specific Customization:</strong> This brag sheet is customized specifically for {recommendation?.recommender_name}.
              You can modify any section and exclude sections that aren't relevant for this teacher.
            </p>
          </div>
        </div>

        {/* Progress Bar - Prominent and Sticky */}
        <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-slate-900">
                Progress: {completionScore}%
              </span>
              {completionScore >= 70 && (
                <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full font-semibold animate-pulse">
                  🎉 Ready to continue!
                </span>
              )}
              {completionScore >= 50 && completionScore < 70 && (
                <span className="text-sm bg-gold-100 text-gold-700 px-3 py-1.5 rounded-full font-semibold">
                  Almost there - {70 - completionScore}% to go
                </span>
              )}
              {completionScore < 50 && completionScore > 0 && (
                <span className="text-sm bg-azure-100 text-azure-700 px-3 py-1.5 rounded-full font-medium">
                  Keep going! {70 - completionScore}% until ready
                </span>
              )}
            </div>
            {saving && (
              <span className="text-sm text-stone-500 flex items-center gap-1">
                <Save className="h-4 w-4 animate-pulse" />
                Saving...
              </span>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-stone-200 rounded-full h-3 overflow-hidden mb-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                completionScore >= 70 ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                completionScore >= 50 ? 'bg-gradient-to-r from-gold-500 to-orange-500' :
                'bg-gradient-to-r from-azure-400 to-blue-500'
              }`}
              style={{ width: `${completionScore}%` }}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSaveAndExit}
              className="px-5 py-2 bg-white border-2 border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2 font-medium"
            >
              <Save className="h-4 w-4" />
              Save & Exit
            </button>

            <div className="flex items-center gap-3">
              {completionScore >= 50 && completionScore < 70 && workflowState !== 'ready' && (
                <button
                  onClick={handleMarkComplete}
                  className="px-5 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors flex items-center gap-2 font-medium"
                >
                  <CheckCircle className="h-4 w-4" />
                  Mark Complete & Exit
                </button>
              )}
              <button
                onClick={handleContinueNext}
                className={`px-6 py-2.5 text-white rounded-lg transition-all flex items-center gap-2 font-semibold ${
                  completionScore >= 70
                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/30 animate-pulse'
                    : 'bg-azure-300 cursor-not-allowed opacity-60'
                }`}
                disabled={completionScore < 70}
                title={completionScore < 70 ? `Complete ${70 - completionScore}% more to continue` : 'Continue to create note'}
              >
                Continue to Create Note
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher-Specific Sections */}
      <div className="space-y-6">
        {/* College Goals */}
        <CollegeGoalsSection
          data={teacherBragSheetData.collegeGoals || ''}
          onChange={(value) => updateField('collegeGoals', value)}
          excluded={teacherBragSheetData.sectionExclusions?.collegeGoals}
          onToggleExclusion={toggleSectionExclusion}
        />

        {/* Classroom Moments */}
        <ClassroomMomentsSection
          moments={teacherBragSheetData.classroomMoments || []}
          onAdd={(moment) => addArrayItem('classroomMoments', moment)}
          onRemove={(index) => removeArrayItem('classroomMoments', index)}
          excluded={teacherBragSheetData.sectionExclusions?.classroomMoments}
          onToggleExclusion={toggleSectionExclusion}
          teacherName={recommendation?.recommender_name}
        />

        {/* Unexpected Details */}
        <UnexpectedDetailsSection
          details={teacherBragSheetData.unexpectedDetails || []}
          onAdd={(detail) => addArrayItem('unexpectedDetails', detail)}
          onRemove={(index) => removeArrayItem('unexpectedDetails', index)}
          excluded={teacherBragSheetData.sectionExclusions?.unexpectedDetails}
          onToggleExclusion={toggleSectionExclusion}
        />

        {/* Thank You Note */}
        <ThankYouNoteSection
          note={teacherBragSheetData.thankYouNote || ''}
          onChange={(value) => updateField('thankYouNote', value)}
          excluded={teacherBragSheetData.sectionExclusions?.thankYouNote}
          onToggleExclusion={toggleSectionExclusion}
          teacherName={recommendation?.recommender_name}
        />
      </div>


      {/* Thematic Review Modal */}
      {showThematicReview && thematicAnalysis && (
        <ThematicReviewModal
          analysis={thematicAnalysis}
          onClose={() => setShowThematicReview(false)}
          intendedMajor={thematicAnalysis.intendedMajor || 'your chosen field'}
        />
      )}
    </div>
  );
};

// College Goals Section Component
function CollegeGoalsSection({ data, onChange, excluded = false, onToggleExclusion }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          <h2 className="text-xl font-semibold">College Goals</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('collegeGoals', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Explain your college goals and target schools
          </label>
          <textarea
            value={data}
            onChange={(e) => onChange(e.target.value)}
            placeholder="I'm applying to mid-tier liberal arts colleges and state universities with strong political science programs. My goal is to study government and public policy..."
            rows={4}
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <p className="text-xs text-stone-500 mt-1">
            Help your teacher understand your academic goals and the types of schools you're targeting.
          </p>
        </div>
      )}
    </div>
  );
}

// Classroom Moments Section Component
function ClassroomMomentsSection({ moments, onAdd, onRemove, excluded = false, onToggleExclusion, teacherName = '' }) {
  const [newMoment, setNewMoment] = useState({ title: '', description: '', type: '' });
  const [selectedType, setSelectedType] = useState('');
  const [typeError, setTypeError] = useState('');

  // Define available moment types
  const momentTypes = [
    { id: 'classroom_moment', label: 'Classroom Moment', placeholder: 'Describe a specific moment in class...' },
    { id: 'growth_example', label: 'Growth Example', placeholder: 'How you grew or overcame a challenge...' },
    { id: 'contribution', label: 'Contribution', placeholder: 'How you contributed to the class...' },
    { id: 'leadership', label: 'Leadership', placeholder: 'When you showed leadership...' },
    { id: 'collaboration', label: 'Collaboration', placeholder: 'How you worked with others...' },
    { id: 'creativity', label: 'Creative Work', placeholder: 'A creative project or idea...' }
  ];

  const handleAdd = () => {
    // Check for duplicate type
    const existingTypes = moments.map(m => m.type);
    if (selectedType && existingTypes.includes(selectedType)) {
      setTypeError(`You already have a ${momentTypes.find(t => t.id === selectedType)?.label} entry. Please choose a different type.`);
      return;
    }

    if (newMoment.title.trim() && newMoment.description.trim() && selectedType) {
      onAdd({ ...newMoment, type: selectedType, id: Date.now() });
      setNewMoment({ title: '', description: '', type: '' });
      setSelectedType('');
      setTypeError('');
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          <h2 className="text-xl font-semibold">
            Specific Classroom Moments {teacherName && `with ${teacherName}`}
          </h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('classroomMoments', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <>
          <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
            <h4 className="font-medium mb-4">Add Classroom Moment</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type of Moment <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    setTypeError('');
                  }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                >
                  <option value="">Select a type...</option>
                  {momentTypes.map(type => {
                    const isUsed = moments.some(m => m.type === type.id);
                    return (
                      <option key={type.id} value={type.id} disabled={isUsed}>
                        {type.label} {isUsed ? '(Already added)' : ''}
                      </option>
                    );
                  })}
                </select>
                {typeError && (
                  <p className="text-red-600 text-sm mt-1">{typeError}</p>
                )}
              </div>

              <input
                placeholder="Moment title (e.g., 'The Constitution Unit (September)')"
                value={newMoment.title}
                onChange={(e) => setNewMoment({...newMoment, title: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <textarea
                placeholder={selectedType ? momentTypes.find(t => t.id === selectedType)?.placeholder : "Describe what happened and how you grew or contributed..."}
                value={newMoment.description}
                onChange={(e) => setNewMoment({...newMoment, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <button
                onClick={handleAdd}
                disabled={!newMoment.title.trim() || !newMoment.description.trim() || !selectedType}
                className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:bg-stone-300"
              >
                Add Moment
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {moments.map((moment, index) => {
              const typeInfo = momentTypes.find(t => t.id === moment.type);
              return (
                <div key={moment.id || index} className="p-4 border border-stone-200 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="font-medium">{moment.title}</h5>
                      {typeInfo && (
                        <span className="inline-block px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full mt-1">
                          {typeInfo.label}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => onRemove(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-600">{moment.description}</p>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-stone-500">
            Share 3-4 specific moments from {teacherName ? `${teacherName}'s` : 'this teacher\'s'} class that show your growth, contributions, or learning process.
          </p>
        </>
      )}
    </div>
  );
}

// Unexpected Details Section Component
function UnexpectedDetailsSection({ details, onAdd, onRemove, excluded = false, onToggleExclusion }) {
  const [newDetail, setNewDetail] = useState({ title: '', description: '' });

  const handleAdd = () => {
    if (newDetail.title.trim() && newDetail.description.trim()) {
      onAdd({ ...newDetail, id: Date.now() });
      setNewDetail({ title: '', description: '' });
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Things You Might Not Know About Me</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('unexpectedDetails', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <>
          <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
            <h4 className="font-medium mb-4">Add Personal Detail</h4>
            <div className="space-y-4">
              <input
                placeholder="Detail title (e.g., 'I'm a first-generation college student')"
                value={newDetail.title}
                onChange={(e) => setNewDetail({...newDetail, title: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <textarea
                placeholder="Brief explanation or story..."
                value={newDetail.description}
                onChange={(e) => setNewDetail({...newDetail, description: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <button
                onClick={handleAdd}
                disabled={!newDetail.title.trim() || !newDetail.description.trim()}
                className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:bg-stone-300"
              >
                Add Detail
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.map((detail, index) => (
              <div key={detail.id || index} className="p-4 border border-stone-200 rounded-lg bg-stone-50">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium text-sm">{detail.title}</h5>
                  <button
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-xs text-slate-600">{detail.description}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-stone-500">
            Share interesting personal details that help your teacher write a more personal recommendation.
          </p>
        </>
      )}
    </div>
  );
}

// Thank You Note Section Component
function ThankYouNoteSection({ note, onChange, excluded = false, onToggleExclusion, teacherName = '' }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Thank You Note</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('thankYouNote', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Personal thank you message {teacherName && `to ${teacherName}`}
          </label>
          <textarea
            value={note}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Thank you for taking the time to write this recommendation${teacherName ? `, ${teacherName}` : ''}. Your class has been instrumental in helping me understand...`}
            rows={4}
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <p className="text-xs text-stone-500 mt-1">
            Express genuine gratitude and mention specific ways this teacher has impacted you.
          </p>
        </div>
      )}
    </div>
  );
}

export default TeacherBragSheetFramework;